import { useMemo, useState } from 'react'
import { useAidStations, useDeleteAidStation } from '../../hooks/useAidStations'
import { EditAidStationDialog } from './EditAidStationDialog'
import { AidStation } from '../../types'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Edit, Trash2, MapPin } from 'lucide-react'

interface AidStationsTableProps {
  runId: string
}

export function AidStationsTable({ runId }: AidStationsTableProps) {
  const { data: aidStations, isLoading } = useAidStations(runId)
  const deleteMutation = useDeleteAidStation()
  const [editingAidStation, setEditingAidStation] = useState<AidStation | null>(null)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this aid station?')) {
      await deleteMutation.mutateAsync(id)
    }
  }

  const handleEdit = (aidStation: AidStation) => {
    setEditingAidStation(aidStation)
    setIsEditDialogOpen(true)
  }

  const handleEditDialogClose = () => {
    setIsEditDialogOpen(false)
    setEditingAidStation(null)
  }

  // Calculate summary information
  const summary = useMemo(() => {
    if (!aidStations || aidStations.length === 0) return null
    
    const totalStations = aidStations.length
    const stationsWithPlannedStops = aidStations.filter(station => 
      station.planned_stop_min && station.planned_stop_min > 0
    ).length
    const totalPlannedStopTime = aidStations.reduce((sum, station) => 
      sum + (station.planned_stop_min || 0), 0
    )
    const uniqueItems = new Set()
    aidStations.forEach(station => {
      if (station.available && Array.isArray(station.available)) {
        (station.available as string[]).forEach(item => uniqueItems.add(item))
      }
    })
    
    return {
      totalStations,
      stationsWithPlannedStops,
      totalPlannedStopTime,
      uniqueItemsCount: uniqueItems.size
    }
  }, [aidStations])

  if (isLoading) return <div>Loading aid stations...</div>
  if (!aidStations || aidStations.length === 0) {
    return (
      <div className="text-center py-8">
        <MapPin className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500">No aid stations yet. Add your first aid station to get started.</p>
      </div>
    )
  }

  return (
    <>
      {/* Desktop Table View */}
      <div className="hidden lg:block rounded-lg border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/40">
              <TableHead className="font-semibold">Name</TableHead>
              <TableHead className="font-semibold">Distance</TableHead>
              <TableHead className="font-semibold">Available Items</TableHead>
              <TableHead className="font-semibold">Planned Stop</TableHead>
              <TableHead className="font-semibold">Notes</TableHead>
              <TableHead className="w-[100px] font-semibold">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {aidStations.map((aidStation) => (
              <TableRow key={aidStation.id} className="hover:bg-muted/50 h-12">
                <TableCell className="font-medium">{aidStation.name}</TableCell>
                <TableCell className="font-mono">{aidStation.at_distance_km} km</TableCell>
            <TableCell>
              <div className="flex flex-wrap gap-1">
                {(aidStation.available as string[])?.map((item, index) => (
                  <Badge key={index} variant="secondary" className="text-xs">
                    {item}
                  </Badge>
                ))}
              </div>
            </TableCell>
                <TableCell className="font-mono">
                  {aidStation.planned_stop_min && aidStation.planned_stop_min > 0 ? `${aidStation.planned_stop_min}min` : '-'}
                </TableCell>
                <TableCell>
                  {aidStation.notes && (
                    <span className="text-sm text-muted-foreground line-clamp-2">
                      {aidStation.notes}
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex space-x-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="h-8 w-8 p-0"
                      title="Edit aid station"
                      onClick={() => handleEdit(aidStation)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleDelete(aidStation.id)}
                      className="h-8 w-8 p-0 hover:bg-destructive hover:text-destructive-foreground"
                      title="Delete aid station"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
          </TableRow>
        ))}
          </TableBody>
          
          {/* Desktop Summary Row */}
          {summary && (
            <tfoot>
              <tr className="border-t-2 bg-muted/50 font-medium">
                <td className="p-3 font-semibold">Summary</td>
                <td className="p-3 font-semibold">{summary.totalStations} stations</td>
                <td className="p-3 font-semibold">{summary.uniqueItemsCount} unique items</td>
                <td className="p-3 font-mono font-semibold">{summary.totalPlannedStopTime}min total</td>
                <td className="p-3 font-semibold">{summary.stationsWithPlannedStops} with stops</td>
                <td className="p-3"></td>
              </tr>
            </tfoot>
          )}
        </Table>
      </div>

      {/* Mobile Card View */}
      <div className="lg:hidden space-y-4">
        {aidStations.map((aidStation) => (
          <div key={aidStation.id} className="rounded-lg border bg-card p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <h3 className="font-medium text-lg">{aidStation.name}</h3>
              </div>
              <div className="flex space-x-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  className="h-8 w-8 p-0"
                  title="Edit aid station"
                  onClick={() => handleEdit(aidStation)}
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleDelete(aidStation.id)}
                  className="h-8 w-8 p-0 hover:bg-destructive hover:text-destructive-foreground"
                  title="Delete aid station"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-sm mb-3">
              <div>
                <span className="text-muted-foreground">Distance:</span>
                <p className="font-mono font-medium">{aidStation.at_distance_km} km</p>
              </div>
              <div>
                <span className="text-muted-foreground">Planned Stop:</span>
                <p className="font-mono font-medium">
                  {aidStation.planned_stop_min && aidStation.planned_stop_min > 0 ? `${aidStation.planned_stop_min}min` : '-'}
                </p>
              </div>
            </div>

            {(aidStation.available as string[])?.length > 0 && (
              <div className="mb-3">
                <span className="text-muted-foreground text-sm">Available Items:</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {(aidStation.available as string[]).map((item, index) => (
                    <Badge key={index} variant="secondary" className="text-xs">
                      {item}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {aidStation.notes && (
              <div className="pt-3 border-t">
                <span className="text-muted-foreground text-sm">Notes:</span>
                <p className="text-sm mt-1">{aidStation.notes}</p>
              </div>
            )}
          </div>
        ))}
        
        {/* Mobile Summary Card */}
        {summary && (
          <div className="rounded-lg border bg-muted/20 p-4">
            <h3 className="font-semibold text-lg mb-3 flex items-center">
              <MapPin className="h-5 w-5 mr-2" />
              Aid Stations Summary
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Total Stations:</span>
                <p className="font-semibold text-lg">{summary.totalStations}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Total Stop Time:</span>
                <p className="font-mono font-semibold text-lg">{summary.totalPlannedStopTime}min</p>
              </div>
              <div>
                <span className="text-muted-foreground">With Planned Stops:</span>
                <p className="font-semibold">{summary.stationsWithPlannedStops}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Unique Items:</span>
                <p className="font-semibold">{summary.uniqueItemsCount}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <EditAidStationDialog
        aidStation={editingAidStation}
        runId={runId}
        open={isEditDialogOpen}
        onOpenChange={handleEditDialogClose}
      />
    </>
  )
}